import React, { useState, useMemo, useEffect } from "react";
import "./Quiz.css";
import { useNavigate } from "react-router-dom";
import mexicoData from "../../assets/data/mexicoStates.json";
import instData from "../../assets/data/institutionsList.json";
import { send } from "@emailjs/browser";

// Safely extract arrays from imported JSON
const mexicoStates = Array.isArray(mexicoData)
  ? mexicoData
  : mexicoData.mexicoStates || [];
const institutionsList = Array.isArray(instData)
  ? instData
  : instData.institutionsList || [];

// Educational levels raw values and display labels
const eduLevels = [
  { value: "basico", label: "Básico" },
  { value: "medio_superior", label: "Medio Superior" },
  { value: "superior", label: "Superior" },
  { value: "licenciatura", label: "Licenciatura" },
  { value: "tsu", label: "TSU" },
  { value: "posgrado", label: "Posgrado" },
  { value: "egresados", label: "Egresados" },
  { value: "cursos_cortos", label: "Cursos Cortos" },
  { value: "abierto", label: "Abierto" },
  { value: "secundaria", label: "Secundaria" },
  { value: "maestria", label: "Maestría" },
  { value: "posdoctorado", label: "Posdoctorado" },
  { value: "tecnico", label: "Técnico" },
];

// Question templates and prompts
const institutionQuestions = [
  (state) =>
    `Genial, ya con ${state} listo 🙌 ¿Qué institución te interesa para ver becas?`,
  (state) =>
    `Perfecto, ahora en ${state}, ¿Qué institución otorgante te gustaría explorar?`,
  (state) =>
    `Super, ya ubicamos ${state} 😎 ¿Qué institución tienes en mente como becadora?`,
];

// Prompts for subsequent steps
const gpaPrompts = {
  "Artes y Diseño":
    "Artista sí, pero ¿También aplicadx? 🎨 ¿Cuál es tu promedio más o menos?",
  "Ciencias Sociales y Humanidades":
    "Sabemos que te gusta analizar todo 🧠. Pero ahora dinos tú, ¿Qué promedio?",
  "Educación y Pedagogía":
    "Educando se aprende, ¿Verdad? 📚 Pero cuéntanos, ¿Qué promedio traes?",
  "General / Interdisciplinaria":
    "Tu perfil es todoterreno 🚀, ¿Qué promedio traes?",
  "Ingeniería, Ciencia y Tecnología":
    "Entre fórmulas y códigos 🤖, ¿Cómo va tu promedio académico?",
  "Negocios, Economía y Finanzas":
    "Buscando la lana 💸📈, ¿Cómo va tu promedio?",
  "Salud y Medicina":
    "Curando el mundo empieza con buenos promedios 🩺✨, ¿Cómo andas tú?",
};
const scholarshipPrompts = {
  "Menos de 7":
    "Sabemos que el promedio no lo es todo 💁🏻‍♀️, ¿Qué tipo de beca te ayudaría más en este momento?",
  "Entre 7 y 8":
    "¡Estás ahí, dándolo todo! 💪 ¿Qué beca te vendría mejor para seguir echándole ganas?",
  "Entre 8 y 9": "Con ese 8 - 9 estás en zona beca 🤓, ¿Cuál te late más?",
  "Más de 9":
    "¡Más de 9! Estás rompiéndola 🔥, ¿Qué tipo de beca estás buscando?",
};

// tus credenciales de EmailJS
const SERVICE_ID = "service_zy_gmail";
const TEMPLATE_ID = "template_4uomomi"; // tu template de auto-reply

export default function Quiz() {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  // Typing and reveal state
  const [displayedQuestion, setDisplayedQuestion] = useState("");
  const [showOptions, setShowOptions] = useState(false);
  const [revealArray, setRevealArray] = useState([]);

  const [userEmail, setUserEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [emailDisabled, setEmailDisabled] = useState(false);

  // Función para armar el cuerpo del mensaje
  const buildMessage = () => {
    // Mapea friendly names para cada pregunta
    const labels = {
      mexicoState: "Estado",
      institution: "Institución",
      educationLevel: "Nivel de estudios",
      interestArea: "Área de interés",
      gpa: "Promedio",
      scholarshipType: "Tipo de beca",
    };
    return Object.entries(answers)
      .filter(([k]) => k !== "emailConsent")
      .map(([k, v]) => {
        const val = Array.isArray(v) ? v.join(", ") : v;
        return `• ${labels[k] || k}: ${val}`;
      })
      .join("\n");
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(userEmail)) {
      setEmailStatus("❌ Ingresa un correo válido.");
      return;
    }

    setEmailStatus("⏳ Enviando...");
    const about = "Recomendaciones para";

    try {
      await send(SERVICE_ID, TEMPLATE_ID, {
        about, // usado en Subject: {{about}}
        name: userEmail, // si lo usas en Subject/Body como {{name}}
        email: userEmail, // 👈 debe llamarse "email" (no to_email)
        message: buildMessage(),
        from_name: "Equipo ZY", // opcional si lo usas en el template
      });

      setEmailStatus("✅ ¡Email enviado! (no olvides revisar tu spam)");
      setEmailDisabled(true);

      setTimeout(() => {
        setEmailStatus("⏳ Cargando resultados...");
        setTimeout(() => {
          navigate("/filtro-becas", { state: { answers } });
        }, 3000);
      }, 4000);
    } catch (err) {
      console.error(err);
      setEmailStatus("❌ Error al enviar. Intenta de nuevo.");
    }
  };

  // Handler para ir atrás
  const handleBack = () => {
    const currentStep = steps[stepIndex];
    // Limpiar la respuesta de este paso
    setAnswers((prev) => {
      const copy = { ...prev };
      delete copy[currentStep.id];
      return copy;
    });
    // Retroceder índice
    setStepIndex((i) => i - 1);
  };

  // Build steps dynamically
  const steps = useMemo(
    () => [
      {
        id: "mexicoState",
        question: "¿En qué estado de México te gustaría estudiar?",
        type: "list",
        options: mexicoStates,
      },
      {
        id: "institution",
        question: institutionQuestions[
          Math.floor(Math.random() * institutionQuestions.length)
        ](answers.mexicoState || "..."),
        type: "list",
        options: institutionsList,
      },
      {
        id: "educationLevel",
        question: `Ya con ${
          answers.institution || "..."
        } en mente 👀 ¿Qué nivel de estudios estás considerando?`,
        type: "checkbox",
        options: eduLevels,
      },
      {
        id: "interestArea",
        question:
          "¡Perfecto! Ahora dime, ¿Qué área te interesa más para seguir con tus estudios?",
        type: "options",
        options: Object.keys(gpaPrompts),
      },
      {
        id: "gpa",
        question: answers.interestArea
          ? gpaPrompts[answers.interestArea]
          : "¿Qué promedio tienes (aproximado)?",
        type: "options",
        options: ["Menos de 7", "Entre 7 y 8", "Entre 8 y 9", "Más de 9"],
      },
      {
        id: "scholarshipType",
        question: answers.gpa
          ? scholarshipPrompts[answers.gpa]
          : "¿Qué tipo de beca te interesa más?",
        type: "options",
        options: ["Académico", "Movilidad", "Apoyo económico", "Otro"],
      },
      {
        id: "emailConsent",
        question: "¿Quieres que te enviemos estas recomendaciones a tu correo?",
        type: "options",
        options: ["Sí", "No"],
      },
      {
        id: "finalEmail",
        question:
          "¡Casi listo!\n\nIngresa tu correo para enviarte tus respuestas:",
        type: "email",
        options: [],
      },
    ],
    [answers]
  );
  const totalSteps = steps.length;
  const step = steps[stepIndex];
  const selectedLevels = answers.educationLevel || [];

  // Efecto de tecleo + reveal
  useEffect(() => {
    let typer;
    setLoading(true);
    setDisplayedQuestion("");
    setShowOptions(false);
    setRevealArray([]);

    const loadTimer = setTimeout(() => {
      setLoading(false);
      const full = step.question;
      if (!full) return;
      setDisplayedQuestion(full.charAt(0));
      let idx = 1;
      typer = setInterval(() => {
        setDisplayedQuestion(full.slice(0, idx + 1));
        idx++;
        if (idx >= full.length) {
          clearInterval(typer);
          setTimeout(() => {
            setShowOptions(true);
            // solo hacemos reveal de lista/opciones si no es email
            if (step.type !== "email") {
              const flags = step.options.map(() => false);
              setRevealArray(flags);
              flags.forEach((_, i) =>
                setTimeout(
                  () =>
                    setRevealArray((prev) => {
                      const c = [...prev];
                      c[i] = true;
                      return c;
                    }),
                  i * 100 + 200
                )
              );
            }
          }, 500);
        }
      }, 30);
    }, 2000);

    return () => {
      clearTimeout(loadTimer);
      if (typer) clearInterval(typer);
    };
  }, [stepIndex, step.question, step.options, step.type]);

  // Avanza de paso, ahora maneja emailConsent y finalEmail
  const handleNext = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    if (id === "emailConsent") {
      if (value === "No") {
        // 1) sólo mostramos el mensaje de carga
        setEmailStatus("⏳ Cargando resultados...");
        // 2) tras 3s, redirigimos
        setTimeout(() => {
          navigate("/filtro-becas", { state: { answers } });
        }, 4000);
        // no avanzamos de paso ni disparamos typing
        return;
      }
      // si dijo Sí, avanzamos al último paso
      return setStepIndex((i) => i + 1);
    }
    // si es el propio finalEmail, no hacemos nada aquí (el form se encarga)
    if (id === "finalEmail") return;
    setStepIndex((i) => i + 1);
  };

  if (
    step.id === "emailConsent" &&
    emailStatus === "⏳ Cargando resultados..."
  ) {
    return (
      <div className="quiz-container">
        <div className="quiz-card">
          {/* Encabezado extra: Quiz Finalizado */}
          <h2 className="quiz-finished-title">🎉 Quiz Finalizado 🎉</h2>
          {/* Mensaje de carga */}
          <p className="contact-status">{emailStatus}</p>
        </div>
      </div>
    );
  }

  // **Render principal único**, sin if(finished)…
  return (
    <div className="quiz-container">
      <div className="quiz-card">
        {/* Numeración / barra hasta antes de finalEmail */}
        {step.id !== "finalEmail" && (
          <>
            {/* Botón de “Atrás”, visible a partir del segundo paso */}
            {stepIndex > 0 && (
              <button
                className="quiz-button--back"
                onClick={handleBack}
                disabled={!showOptions} // <-- Aquí
              >
                ← Atrás
              </button>
            )}

            <h3 className="quiz-step">
              Paso <strong className="actual-step">{stepIndex + 1}</strong> de{" "}
              {totalSteps - 1}
            </h3>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${((stepIndex + 1) / (totalSteps - 1)) * 100}%`,
                }}
              />
            </div>
          </>
        )}

        {loading ? (
          <div className="loading-indicator">
            <div className="dot" /> <div className="dot" />{" "}
            <div className="dot" />
          </div>
        ) : (
          <>
            {/* Pregunta tecleada */}
            <p className="quiz-question">{displayedQuestion}</p>

            {step.type === "email" && showOptions ? (
              <form className="quiz-email-form" onSubmit={handleSendEmail}>
                <input
                  className="quiz-email-input reveal-up"
                  type="email"
                  placeholder="tu@correo.com"
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setEmailStatus("");
                  }}
                  required
                />
                <button
                  className={`quiz-button reveal-up ${
                    emailDisabled ? "disabled" : ""
                  }`}
                  disabled={emailDisabled}
                >
                  Enviar por email
                </button>
                {emailStatus && (
                  <p className="contact-status reveal-up">{emailStatus}</p>
                )}
              </form>
            ) : (
              <div className="quiz-input">
                {/* tu <select> */}
                {step.type === "list" && showOptions && (
                  <select
                    key={step.id}
                    value={answers[step.id] || ""}
                    onChange={(e) => handleNext(step.id, e.target.value)}
                  >
                    <option value="" disabled>
                      Selecciona...
                    </option>
                    {step.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                )}

                {/* tus opciones buttons */}
                {step.type === "options" && showOptions && (
                  <ul className="quiz-options">
                    {step.options.map(
                      (opt, i) =>
                        revealArray[i] && (
                          <li key={opt} className="reveal-up">
                            <button
                              className="quiz-button"
                              onClick={() => handleNext(step.id, opt)}
                            >
                              {opt}
                            </button>
                          </li>
                        )
                    )}
                  </ul>
                )}
                {/* Mostrar cualquier estado de carga o mensaje */}
                {step.id === "emailConsent" && emailStatus && (
                  <p className="contact-status reveal-up">{emailStatus}</p>
                )}

                {/* tus checkboxes */}
                {step.type === "checkbox" && showOptions && (
                  <>
                    <div className="checkbox-grid">
                      {step.options.map(
                        ({ value, label }, i) =>
                          revealArray[i] && (
                            <label
                              key={value}
                              className={`checkbox-pill ${
                                selectedLevels.includes(value) ? "selected" : ""
                              } reveal-up`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedLevels.includes(value)}
                                onChange={(e) => {
                                  const prev = [...selectedLevels];
                                  const upd = e.target.checked
                                    ? [...prev, value]
                                    : prev.filter((v) => v !== value);
                                  setAnswers((p) => ({
                                    ...p,
                                    educationLevel: upd,
                                  }));
                                }}
                              />
                              {label}
                            </label>
                          )
                      )}
                    </div>
                    <div className="quiz-nav">
                      <button
                        className="quiz-button quiz-button--next"
                        disabled={!selectedLevels.length}
                        onClick={() =>
                          handleNext("educationLevel", selectedLevels)
                        }
                      >
                        Siguiente
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
        <hr className="quiz-footer-divider" />
        <p className="quiz-footer-text">Impulsado por IA</p>
      </div>
    </div>
  );
}
